import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpOrderService from "@plannel/services/fp/fp-order-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * 판매 주문(Demand/Sales Order) 조회/선택 전용 팝업 — old `DemandPopup.jsx` 대응. 관리는 이
 * 화면 자체가 아니라 Order.js의 DemandTab에서 하므로 선택 전용(ItemPopup.js와 동일 구성).
 * 목록은 DemandTab이 쓰는 것과 같은 조회(`fpOrderService.getDemands`)를 재사용 — old도 동일하게
 * customerCdNm/inventoryCdNm/dmndCdNm 검색조건 없이 planScope 전량 로드 후 팝업 내부 그리드
 * 자체 필터(`autoFilter`)에 의존했으므로, 서버 재조회 조건 없이 planScope만 전달한다.
 *
 * props: open, onClose(), planScope, onSelect({dmndCd, inventoryCd, inventoryNm, customerCd, dueDate, dueDateFence})
 */
const DemandPopup = ({ t, open, onClose, planScope, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_SALES_ORDER_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_SO_CD"), field: "dmndCd", colId: "dmndCd", width: 160, editable: false },
    { headerName: t("FP_INVENTORY_CD"), field: "inventoryCd", colId: "inventoryCd", width: 160, editable: false },
    { headerName: t("FP_INVENTORY_NAME"), field: "inventoryNm", colId: "inventoryNm", width: 200, editable: false },
    { headerName: t("FP_CUSTOMER_CD"), field: "customerCd", colId: "customerCd", width: 160, editable: false },
    { headerName: t("FP_DUE_DT"), field: "dueDate", colId: "dueDate", width: 160, type: ["centerAligned"], editable: false,
      valueFormatter: (p) => dateUtils.formatDateTime(p.value) },
    { headerName: t("FP_DUE_DT_FENCE"), field: "dueDateFence", colId: "dueDateFence", width: 160, type: ["centerAligned"], editable: false,
      valueFormatter: (p) => dateUtils.formatDateTime(p.value) },
    { headerName: t("FP_DESC_TXT"), field: "descTxt", colId: "descTxt", width: 110, editable: false },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t]);

  const loadDemands = () => {
    if (!planScope) return;
    fpOrderService.getDemands({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadDemands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({
      dmndCd: e.data.dmndCd, inventoryCd: e.data.inventoryCd, inventoryNm: e.data.inventoryNm,
      customerCd: e.data.customerCd, dueDate: e.data.dueDate, dueDateFence: e.data.dueDateFence
    });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("FP_SALES_ORDER_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 480, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(DemandPopup);
